import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

function formatTime(value) {
  const d = new Date(value);
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function AgentMessagesModal({ client, onClose, onRead }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const dialogRef = useRef(null);
  const clientId = client.agent_client_id;
  const name = `${client.first_name || ''} ${client.last_name || ''}`.trim() || client.email || 'Client';

  // Escape closes the modal; Tab/Shift+Tab is trapped inside it; focus
  // returns to whatever triggered the modal once it closes.
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const closeButton = dialogRef.current?.querySelector('[data-modal-close]');
    (closeButton || dialogRef.current)?.focus();

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll(
        'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), '
        + 'textarea:not(:disabled), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus();
    };
  }, [onClose]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }));
  }, []);

  useEffect(() => {
    let active = true;
    api.get(`/agent-clients/${clientId}/messages`)
      .then((res) => {
        if (!active) return;
        setMessages(res.data.messages || []);
        onRead?.(clientId);
        scrollToBottom();
      })
      .catch(() => toast.error('Could not load messages'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [clientId, onRead, scrollToBottom]);

  const send = async () => {
    const content = draft.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const res = await api.post(`/agent-clients/${clientId}/messages`, { content });
      setMessages((m) => [...m, res.data.message]);
      setDraft('');
      scrollToBottom();
    } catch {
      toast.error('Failed to send');
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Messages with ${name}`}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card, #fff)', borderRadius: 12, width: '100%', maxWidth: 520,
          maxHeight: '80vh', display: 'flex', flexDirection: 'column',
          border: '1px solid var(--border)', boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 16px', borderBottom: '1px solid var(--border)',
        }}>
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{name}</div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Direct messages</div>
          </div>
          <button type="button" data-modal-close className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 16, minHeight: 200 }}>
          {loading ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div>
          ) : messages.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', textAlign: 'center', marginTop: 24 }}>
              No messages yet. Start the conversation with {client.first_name || 'your client'}.
            </div>
          ) : (
            messages.map((m) => {
              const mine = m.sender_role === 'agent';
              return (
                <div key={m.id} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
                  <div style={{ maxWidth: '78%' }}>
                    <div style={{
                      padding: '8px 12px', borderRadius: 12,
                      background: mine ? 'var(--accent, #C2410C)' : 'var(--bg-hover, #f4eee4)',
                      color: mine ? '#fff' : 'var(--text-primary)',
                      fontSize: 13, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    }}>
                      {m.content}
                    </div>
                    <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3, textAlign: mine ? 'right' : 'left' }}>
                      {formatTime(m.created_at)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        <div style={{ display: 'flex', gap: 8, padding: 12, borderTop: '1px solid var(--border)' }}>
          <textarea
            className="form-input"
            placeholder={`Message ${client.first_name || 'client'}…`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKeyDown}
            rows={2}
            style={{ flex: 1, resize: 'none' }}
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={send}
            disabled={sending || !draft.trim()}
            style={{ alignSelf: 'flex-end' }}
          >
            <Send size={14} /> Send
          </button>
        </div>
      </div>
    </div>
  );
}
